import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import zh from './locales/zh.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import vi from './locales/vi.json';
import es from './locales/es.json';

export const SUPPORTED_LOCALES = ['en', 'zh', 'de', 'fr', 'ko', 'ja', 'vi', 'es'] as const;
export type SupportedLocale = typeof SUPPORTED_LOCALES[number];

export const LOCALE_NAMES: Record<SupportedLocale, string> = {
  en: 'English',
  zh: '中文',
  de: 'Deutsch',
  fr: 'Français',
  ko: '한국어',
  ja: '日本語',
  vi: 'Tiếng Việt',
  es: 'Español'
};

// `?lang=` from the native apps wins (iOS/Android pin it per build target: China build → zh,
// international build → en). It is not persisted, so a plain web visit keeps its own choice.
export function getLocaleFromURL(): SupportedLocale | null {
  const lang = new URLSearchParams(window.location.search).get('lang');
  return lang && SUPPORTED_LOCALES.includes(lang as SupportedLocale) ? (lang as SupportedLocale) : null;
}

// URL param → saved locale → English
function getInitialLocale(): SupportedLocale {
  const fromURL = getLocaleFromURL();
  if (fromURL) return fromURL;
  const saved = localStorage.getItem('fs_locale');
  if (saved && SUPPORTED_LOCALES.includes(saved as SupportedLocale)) {
    return saved as SupportedLocale;
  }
  // Default to English
  return 'en';
}

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      zh: { translation: zh },
      ja: { translation: ja },
      ko: { translation: ko },
      de: { translation: de },
      fr: { translation: fr },
      vi: { translation: vi },
      es: { translation: es }
    },
    lng: getInitialLocale(),
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    }
  });

// Change language and save to localStorage
export function changeLanguage(locale: SupportedLocale): void {
  i18n.changeLanguage(locale);
  localStorage.setItem('fs_locale', locale);
}

export function getCurrentLocale(): SupportedLocale {
  return i18n.language as SupportedLocale;
}

export default i18n;
